import React from 'react';
import { CreditCard, Lock, Receipt } from 'lucide-react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@sandtech-hub-monorepo/ui-kit';
import { useCognito } from '@sandtech-hub-monorepo/ui-auth-kit';

import { useLearnerPath } from '../../data/useLearnerPath';
import { PasswordTab } from './PasswordTab';
import { PaymentsTab } from './PaymentsTab';
import { SubscriptionTab } from './SubscriptionTab';

const TRIGGER_CLASS =
  'flex items-center gap-2 whitespace-nowrap py-3 data-[state=active]:border-heading data-[state=active]:border-b-2 data-[state=active]:text-heading text-muted-foreground font-semibold rounded-none';

/**
 * The one page both learners share, and the only one that had to be split
 * rather than switched: Payments is the degree semester payment plan, an
 * instalment schedule against tuition, and Subscription is the Stripe
 * All-Access charge. Each is shown only to the learner it can apply to, so
 * neither is offered a billing page describing a product they cannot hold.
 */
export const SettingsPage = () => {
  const path = useLearnerPath();
  const { isFederatedUser } = useCognito();

  const showPayments = path !== 'gsa';
  const showSubscription = path !== 'degree';
  // Scoped to GSA learners only, same as Subscription, per product request.
  // Also hidden for anyone who signed in through an identity provider (eHub
  // SSO, Google, etc.) rather than a Cognito password - there is no password
  // on the account for this form to change.
  const showPassword = path !== 'degree' && !isFederatedUser;

  return (
    <div className="mx-auto m-6 flex max-w-screen-2xl flex-col gap-8 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-8">
      <Tabs
        defaultValue={showPayments ? 'payments' : 'subscription'}
        className="flex flex-col gap-8"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="font-semibold text-[32px] leading-[42px] text-foreground">
              Account
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage your billing and account security.
            </p>
          </div>
          {/*
            Horizontally scrollable rather than wrapping: three triggers with
            icons is tight on a narrow phone, and a wrapped second row of
            tabs reads as a second, unrelated tab group.
          */}
          <TabsList className="h-auto w-full justify-start overflow-x-auto rounded-none border-b border-muted bg-transparent p-0">
            {showPayments && (
              <TabsTrigger value="payments" className={TRIGGER_CLASS}>
                <Receipt className="h-4 w-4" />
                Payments
              </TabsTrigger>
            )}
            {showSubscription && (
              <TabsTrigger value="subscription" className={TRIGGER_CLASS}>
                <CreditCard className="h-4 w-4" />
                Subscription
              </TabsTrigger>
            )}
            {showPassword && (
              <TabsTrigger value="password" className={TRIGGER_CLASS}>
                <Lock className="h-4 w-4" />
                Password
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        {showPayments && (
          <TabsContent value="payments" className="mt-0">
            <PaymentsTab />
          </TabsContent>
        )}
        {showSubscription && (
          <TabsContent value="subscription" className="mt-0">
            <SubscriptionTab />
          </TabsContent>
        )}
        {showPassword && (
          <TabsContent value="password" className="mt-0">
            <PasswordTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
